import React, { useEffect } from 'react';
import classnames from 'classnames';
import DaApp from '@wix/da-shared-react/pkg/DaApp';
import SiteHeader from '@wix/da-shared-react/pkg/SiteHeader';
import { RuntimeEnvironment } from '@wix/da-shared-react/pkg/types';
import { ThemeOptions } from '@wix/da-react-context/pkg/ThemeContext';
import SiteFooter from '@wix/da-shared-react/pkg/SiteFooter';
import DrawerLoadable from '@wix/da-feedback-shared/pkg/Drawer/DrawerLoadable';
import FeedbackPageLoadable from '@wix/da-feedback-shared/pkg/FeedbackPage/FeedbackPageLoadable';
import { composeModalRenderers } from '@wix/da-shared-react/pkg/Modals/helpers';
import { adoptsModalRenderer } from '@wix/da-adopts-shared/pkg/project-tools';
import { submitModalRenderer } from '@wix/da-submit-shared/pkg/project-tools';
import { muroModalRenderer } from '@wix/da-muro-ui/pkg/project-tools';
import DamzDrawerLoadable from '@wix/da-damz-shared/pkg/components/Drawer/DrawerLoadable';
import DamzPageLoadable from '@wix/da-damz-shared/pkg/components/DamzPage/DamzPageLoadable';
import DamzGlobalLoadable from '@wix/da-damz-shared/pkg/components/DamzGlobal/DamzGlobalLoadable';
import { damzModalRenderer } from '@wix/da-damz-shared/pkg/project-tools';
import CommentPage from '../CommentPage';
import { MuroDepContext } from '@wix/da-react-context/pkg/MuroDepContext';
import MicroMuro from '@wix/da-muro-ui/pkg/components/MuroEditor/preset/Micro';
import MiniMuro from '@wix/da-muro-ui/pkg/components/MuroEditor/preset/Mini';
import MobileMuro from '@wix/da-muro-ui/pkg/components/MuroEditor/preset/Mobile';

const MuroDepValue = {
  MicroComponent: MicroMuro,
  MiniComponent: MiniMuro,
  MobileComponent: MobileMuro,
};

import s from './App.scss';

export interface Props {
  language: string;
  environment?: RuntimeEnvironment;
  isEmbed?: boolean;
  appLoaded: () => void;
}

const App: React.FC<Props> = ({
  language,
  environment,
  isEmbed,
  appLoaded,
}) => {
  useEffect(() => {
    appLoaded();
  }, [appLoaded]);

  const appProps = {
    language,
    environment,
    ncContextProps: {
      PageComponent: FeedbackPageLoadable,
      DrawerComponent: DrawerLoadable,
    },
  };

  if (isEmbed) {
    // just the basics for embeds and forced light theme
    return (
      <DaApp
        disableHeadTags
        {...appProps}
        bodyClassName={classnames('basic', s['embed'])}
        theme={ThemeOptions.LIGHT}
      >
        <CommentPage isInfinite />
      </DaApp>
    );
  }

  return (
    <MuroDepContext.Provider value={MuroDepValue}>
      <DaApp
        {...appProps}
        modalsRenderFunc={composeModalRenderers(
          adoptsModalRenderer,
          muroModalRenderer,
          submitModalRenderer,
          damzModalRenderer as any
        )}
        damzContextProps={{
          GlobalComponent: DamzGlobalLoadable,
          DrawerComponent: DamzDrawerLoadable,
          PageComponent: DamzPageLoadable,
        }}
        bodyClassName={s['root']}
        disableHeadTags
      >
        <div className={s['header']}>
          <SiteHeader />
        </div>
        <CommentPage isCanonical />
        <SiteFooter />
      </DaApp>
    </MuroDepContext.Provider>
  );
};

export default App;
